import { MutationTree } from 'vuex';
import {
  UiState,
  CreatorScope,
  AiShellSettings,
  AiShellAttachment,
  AiShellActivityFlags,
  AiShellViewMode,
  AiShellPreviewRole,
  AiShellDataSourceMode,
  AiShellWidgetSummary,
  AiShellStreamingCreation,
  StreamingActivityStatus
} from './state';

// eslint-disable-next-line no-shadow
export const enum MutationTypes {
  SET_AI_SHELL_OPEN = 'SET_AI_SHELL_OPEN',
  SET_AI_SHELL_SCOPE = 'SET_AI_SHELL_SCOPE',
  SET_AI_SHELL_ENTITY_ID = 'SET_AI_SHELL_ENTITY_ID',
  SET_AI_SHELL_ENTITY_NAME = 'SET_AI_SHELL_ENTITY_NAME',
  SET_AI_SHELL_ENTITY_DESC = 'SET_AI_SHELL_ENTITY_DESC',
  SET_AI_SHELL_ENTITY_ICON = 'SET_AI_SHELL_ENTITY_ICON',
  SET_AI_SHELL_ENTITY_COLOR = 'SET_AI_SHELL_ENTITY_COLOR',
  SET_AI_SHELL_CONTEXT = 'SET_AI_SHELL_CONTEXT',
  SET_AI_SHELL_SETTINGS = 'SET_AI_SHELL_SETTINGS',
  SET_AI_SHELL_ATTACHMENTS = 'SET_AI_SHELL_ATTACHMENTS',
  ADD_AI_SHELL_ATTACHMENT = 'ADD_AI_SHELL_ATTACHMENT',
  REMOVE_AI_SHELL_ATTACHMENT = 'REMOVE_AI_SHELL_ATTACHMENT',
  SET_AI_SHELL_ACTIVITY_FLAGS = 'SET_AI_SHELL_ACTIVITY_FLAGS',
  SET_AI_SHELL_PENDING_GENERATION = 'SET_AI_SHELL_PENDING_GENERATION',
  SET_AI_SHELL_HAS_UNSAVED_CHANGES = 'SET_AI_SHELL_HAS_UNSAVED_CHANGES',
  SET_AI_SHELL_SAVE_REQUESTED = 'SET_AI_SHELL_SAVE_REQUESTED',
  SET_AI_SHELL_VIEW_MODE = 'SET_AI_SHELL_VIEW_MODE',
  SET_AI_SHELL_PREVIEW_ROLE = 'SET_AI_SHELL_PREVIEW_ROLE',
  SET_AI_SHELL_DATA_SOURCE_MODE = 'SET_AI_SHELL_DATA_SOURCE_MODE',
  SET_AI_SHELL_WIDGET_SUMMARY = 'SET_AI_SHELL_WIDGET_SUMMARY',
  SET_AI_SHELL_PROGRAM_EDIT_APPLIED = 'SET_AI_SHELL_PROGRAM_EDIT_APPLIED',
  SET_AI_SHELL_STREAMING_CREATION = 'SET_AI_SHELL_STREAMING_CREATION',
  UPDATE_AI_SHELL_STREAMING_ACTIVITY = 'UPDATE_AI_SHELL_STREAMING_ACTIVITY',
  SET_AI_SHELL_INITIAL_PROMPT = 'SET_AI_SHELL_INITIAL_PROMPT'
}

export const mutations: MutationTree<UiState> = {
  [MutationTypes.SET_AI_SHELL_OPEN](state, value: boolean) {
    state.aiShellOpen = value;
  },
  [MutationTypes.SET_AI_SHELL_SCOPE](state, value: CreatorScope) {
    state.aiShellScope = value;
  },
  [MutationTypes.SET_AI_SHELL_ENTITY_ID](state, value: string | null) {
    state.aiShellEntityId = value;
  },
  [MutationTypes.SET_AI_SHELL_ENTITY_NAME](state, value: string | null) {
    state.aiShellEntityName = value;
  },
  [MutationTypes.SET_AI_SHELL_ENTITY_DESC](state, value: string | null) {
    state.aiShellEntityDesc = value;
  },
  [MutationTypes.SET_AI_SHELL_ENTITY_ICON](state, value: string | null) {
    state.aiShellEntityIcon = value;
  },
  [MutationTypes.SET_AI_SHELL_ENTITY_COLOR](state, value: string | null) {
    state.aiShellEntityColor = value;
  },
  [MutationTypes.SET_AI_SHELL_CONTEXT](state, value: Record<string, unknown> | null) {
    state.aiShellContext = value;
  },
  [MutationTypes.SET_AI_SHELL_SETTINGS](state, value: Partial<AiShellSettings>) {
    state.aiShellSettings = { ...state.aiShellSettings, ...value };
  },
  [MutationTypes.SET_AI_SHELL_ATTACHMENTS](state, value: AiShellAttachment[]) {
    state.aiShellAttachments = value;
  },
  [MutationTypes.ADD_AI_SHELL_ATTACHMENT](state, value: AiShellAttachment) {
    state.aiShellAttachments.push(value);
  },
  [MutationTypes.REMOVE_AI_SHELL_ATTACHMENT](state, id: string) {
    state.aiShellAttachments = state.aiShellAttachments.filter(a => a.id !== id);
  },
  [MutationTypes.SET_AI_SHELL_ACTIVITY_FLAGS](state, value: Partial<AiShellActivityFlags>) {
    state.aiShellActivityFlags = { ...state.aiShellActivityFlags, ...value };
  },
  [MutationTypes.SET_AI_SHELL_PENDING_GENERATION](state, value: Record<string, unknown> | null) {
    state.aiShellPendingGeneration = value;
  },
  [MutationTypes.SET_AI_SHELL_HAS_UNSAVED_CHANGES](state, value: boolean) {
    state.aiShellHasUnsavedChanges = value;
  },
  [MutationTypes.SET_AI_SHELL_SAVE_REQUESTED](state, value: boolean) {
    state.aiShellSaveRequested = value;
  },
  [MutationTypes.SET_AI_SHELL_VIEW_MODE](state, value: AiShellViewMode) {
    state.aiShellViewMode = value;
  },
  [MutationTypes.SET_AI_SHELL_PREVIEW_ROLE](state, value: AiShellPreviewRole) {
    state.aiShellPreviewRole = value;
  },
  [MutationTypes.SET_AI_SHELL_DATA_SOURCE_MODE](state, value: AiShellDataSourceMode) {
    state.aiShellDataSourceMode = value;
  },
  [MutationTypes.SET_AI_SHELL_WIDGET_SUMMARY](state, value: AiShellWidgetSummary | null) {
    state.aiShellWidgetSummary = value;
  },
  [MutationTypes.SET_AI_SHELL_PROGRAM_EDIT_APPLIED](state, value: boolean) {
    state.aiShellProgramEditApplied = value;
  },
  [MutationTypes.SET_AI_SHELL_STREAMING_CREATION](state, value: Partial<AiShellStreamingCreation>) {
    state.aiShellStreamingCreation = { ...state.aiShellStreamingCreation, ...value };
  },
  [MutationTypes.UPDATE_AI_SHELL_STREAMING_ACTIVITY](
    state,
    payload: { index: number; status: StreamingActivityStatus }
  ) {
    if (
      payload.index >= 0 &&
      payload.index < state.aiShellStreamingCreation.activityStatuses.length
    ) {
      state.aiShellStreamingCreation.activityStatuses.splice(payload.index, 1, payload.status);
      state.aiShellStreamingCreation.currentActivityIndex = payload.index;
    }
  },
  [MutationTypes.SET_AI_SHELL_INITIAL_PROMPT](state, value: string | null) {
    state.aiShellInitialPrompt = value;
  }
};
